(() => {
  const clamp = (value, min = 0, max = 1) => {
    return Math.min(Math.max(value, min), max);
  };

  const padNumber = (number, padding) => {
    if (!padding) return String(number);
    return String(number).padStart(padding, '0');
  };

  const isMobile = () => {
    return window.matchMedia('(max-width: 1023px)').matches;
  };

  const initSequence = (sequence) => {
    if (!sequence) return null;

    const image = sequence.querySelector('[data-custom-3d-sequence-image]');
    if (!image) return null;

    const frameUrlPattern = sequence.dataset.frameUrlPattern || '';
    const frameCount = Math.max(1, Number(sequence.dataset.frameCount || 1));
    const frameStart = Number(sequence.dataset.frameStart || 1);
    const framePadding = Number(sequence.dataset.framePadding || 0);
    const preloadRange = Math.max(1, Number(sequence.dataset.preloadRange || 12));

    if (!frameUrlPattern.includes('{index}')) return null;

    const loadedFrames = new Set();
    let currentFrame = -1;

    const getFrameUrl = (frameIndex) => {
      const realFrameNumber = frameStart + frameIndex;
      const formattedIndex = padNumber(realFrameNumber, framePadding);

      return frameUrlPattern.replace('{index}', formattedIndex);
    };

    const preloadFrame = (frameIndex) => {
      if (frameIndex < 0 || frameIndex >= frameCount) return;
      if (loadedFrames.has(frameIndex)) return;

      loadedFrames.add(frameIndex);

      const preloadImage = new Image();
      preloadImage.src = getFrameUrl(frameIndex);
    };

    const preloadAround = (frameIndex) => {
      for (let offset = -preloadRange; offset <= preloadRange; offset += 1) {
        preloadFrame(frameIndex + offset);
      }
    };

    const setProgress = (progress) => {
      const nextFrame = Math.round(progress * (frameCount - 1));

      if (nextFrame === currentFrame) return;

      currentFrame = nextFrame;
      image.src = getFrameUrl(currentFrame);
      preloadAround(currentFrame);
    };

    image.src = getFrameUrl(0);
    preloadAround(0);

    return {
      setProgress
    };
  };

  const renderStackedLetters = (textElement) => {
    const originalText = textElement.dataset.text || textElement.textContent || '';

    textElement.innerHTML = '';

    Array.from(originalText).forEach((char) => {
      const span = document.createElement('span');

      if (char === ' ') {
        span.className = 'custom-3d-horizontal-scroll__space';
        span.setAttribute('aria-hidden', 'true');
      } else {
        span.className = 'custom-3d-horizontal-scroll__letter';
        span.textContent = char;
      }

      textElement.appendChild(span);
    });
  };

  const renderInlineText = (textElement) => {
    textElement.textContent = textElement.dataset.text || textElement.textContent || '';
  };

  const initCustom3dHorizontalScroll = (section) => {
    if (section.dataset.custom3dHorizontalInitialized === 'true') return;
    section.dataset.custom3dHorizontalInitialized = 'true';

    const track = section.querySelector('[data-custom-3d-horizontal-track]');
    const text = section.querySelector('[data-custom-3d-horizontal-text]');
    const sequenceElement = section.querySelector('[data-custom-3d-sequence]');
    const sequence = initSequence(sequenceElement);

    if (!track || !text || !sequence) return;

    let textTravel = 0;
    let movementAxis = 'x';
    let lastVisibleState = null;
    let lastTextMode = null;

    const isVisible = () => {
      return section.offsetParent !== null && section.getBoundingClientRect().width > 0;
    };

    const getMovementAxis = () => {
      const mobileDirection = section.dataset.mobileTextDirection || 'horizontal';

      if (isMobile() && mobileDirection === 'vertical') {
        return 'y';
      }

      return 'x';
    };

    const syncTextMode = () => {
      const nextMode = getMovementAxis() === 'y' ? 'vertical' : 'horizontal';

      if (nextMode === lastTextMode) return;

      lastTextMode = nextMode;

      if (nextMode === 'vertical') {
        renderStackedLetters(text);
      } else {
        renderInlineText(text);
      }
    };

    const setSectionHeight = () => {
      if (!isVisible()) return;

      syncTextMode();

      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const minScrollHeight = Number(section.dataset.minScrollHeight || 300);

      movementAxis = getMovementAxis();

      if (movementAxis === 'y') {
        const textHeight = text.scrollHeight || text.getBoundingClientRect().height;
        const sidePadding = viewportHeight * 0.1;

        textTravel = Math.max(0, textHeight - viewportHeight + sidePadding);
      } else {
        const textWidth = text.scrollWidth || text.getBoundingClientRect().width;
        const sidePadding = viewportWidth * 0.1;

        textTravel = Math.max(0, textWidth - viewportWidth + sidePadding);
      }

      const dynamicHeight = viewportHeight + textTravel;
      const minimumHeight = viewportHeight * (minScrollHeight / 100);

      section.style.height = `${Math.max(dynamicHeight, minimumHeight)}px`;
    };

    const update = () => {
      if (!isVisible()) return;

      const rect = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const scrollableDistance = Math.max(1, section.offsetHeight - viewportHeight);
      const progress = clamp(-rect.top / scrollableDistance);

      const translate = textTravel * progress * -1;

      if (movementAxis === 'y') {
        track.style.transform = `translate3d(0, ${translate}px, 0)`;
      } else {
        track.style.transform = `translate3d(${translate}px, 0, 0)`;
      }

      sequence.setProgress(progress);
    };

    let ticking = false;

    const requestUpdate = () => {
      if (ticking) return;

      ticking = true;

      window.requestAnimationFrame(() => {
        update();
        ticking = false;
      });
    };

    const refresh = () => {
      setSectionHeight();
      requestUpdate();
    };

    const watchVisibility = () => {
      const visible = isVisible();

      if (visible !== lastVisibleState) {
        lastVisibleState = visible;

        if (visible) {
          window.requestAnimationFrame(() => {
            refresh();
          });
        }
      }
    };

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(refresh).catch(refresh);
    }

    refresh();

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', refresh);

    const observer = new MutationObserver(() => {
      watchVisibility();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['style', 'class']
    });

    const interval = window.setInterval(watchVisibility, 250);

    document.addEventListener('gatedSections:opened', () => {
      window.requestAnimationFrame(() => {
        refresh();
      });
    });

    document.addEventListener('shopify:section:load', () => {
      window.requestAnimationFrame(() => {
        refresh();
      });
    });

    document.addEventListener('shopify:section:unload', (event) => {
      if (!event.target.contains(section)) return;

      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', refresh);
      observer.disconnect();
      window.clearInterval(interval);
    });
  };

  const initAllCustom3dHorizontalScroll = () => {
    document.querySelectorAll('[data-custom-3d-horizontal-scroll]').forEach(initCustom3dHorizontalScroll);
  };

  document.addEventListener('DOMContentLoaded', initAllCustom3dHorizontalScroll);

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[data-custom-3d-horizontal-scroll]');

    if (section) {
      initCustom3dHorizontalScroll(section);
    }
  });
})();